import { useId } from "react";
import { cn } from "@/lib/cn";

/**
 * Sparkline DECORATIVO (sin datos) · ondas fijas del diseño de referencia, solo
 * como adorno visual de la tarjeta de KPI · una forma distinta por card. `shape`:
 *   · "a" · rampa ascendente (dip suave → sube a la derecha).
 *   · "b" · campana (sube y baja · S de resultado).
 *   · "c" · montaña (pico al centro y desciende).
 *   · "d" · descendente ondulada (baja con rebote).
 *   · "flat" · línea plana (pólizas/próximas · sin actividad).
 * `color` decide el trazo/relleno. `aria-hidden` porque no comunica dato.
 */
const PATHS = {
  a: "M 0 35 Q 25 10, 50 25 T 100 5",
  b: "M 0 30 Q 30 38, 60 15 T 100 8",
  c: "M 0 22 Q 25 6, 50 20 T 100 30",
  d: "M 0 10 Q 30 26, 55 16 Q 78 8, 100 28",
} as const;

export function Sparkline({
  shape = "flat",
  color = "var(--color-slate-400)",
  className,
}: {
  shape?: "a" | "b" | "c" | "d" | "flat";
  color?: string;
  className?: string;
}) {
  const gradId = useId();
  const W = 100;
  const H = 40;

  if (shape === "flat") {
    return (
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className={cn("overflow-visible", className)}
        aria-hidden="true"
      >
        <line
          x1="0"
          y1={H / 2}
          x2={W}
          y2={H / 2}
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          opacity="0.4"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    );
  }

  const linePath = PATHS[shape];
  const areaPath = `${linePath} L ${W},${H} L 0,${H} Z`;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      className={cn("overflow-visible", className)}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${gradId})`} />
      <path
        d={linePath}
        fill="none"
        stroke={color}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
